import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Send } from "lucide-react";
import { API_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function WidgetChat() {
  const { businessId } = useParams();
  const tokenKey = `widget_token_${businessId}`;
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(tokenKey) || "");
  const [messages, setMessages] = useState([]);
  const [startForm, setStartForm] = useState({ name: "", email: "", message: "" });
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/public/widget/${businessId}/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ enabled: false }));
  }, [businessId]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/public/widget/${businessId}/messages`, { params: { token } });
      setMessages(data.messages);
    } catch {
      localStorage.removeItem(tokenKey);
      setToken("");
    }
  }, [businessId, token, tokenKey]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const start = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API_URL}/public/widget/${businessId}/start`, startForm);
      localStorage.setItem(tokenKey, data.token);
      setToken(data.token);
    } catch {
      setError("Could not start chat. Please try again.");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    const text = reply;
    setReply("");
    await axios.post(`${API_URL}/public/widget/${businessId}/reply`, { token, body: text }).catch(() => {});
    load();
  };

  if (!config)
    return <div className="h-screen flex items-center justify-center text-sm text-neutral-400 bg-white">Loading…</div>;
  if (!config.enabled)
    return (
      <div className="h-screen flex items-center justify-center text-sm text-neutral-400 bg-white" data-testid="widget-disabled">
        Chat is currently unavailable.
      </div>
    );

  return (
    <div className="h-screen bg-white flex flex-col" data-testid="widget-chat">
      <div className="bg-neutral-900 text-white px-4 py-3.5 flex items-center gap-2 shrink-0">
        <MessageCircle className="h-4 w-4" />
        <span className="font-heading font-bold text-sm" data-testid="widget-business-name">{config.business_name}</span>
      </div>
      {!token ? (
        <form onSubmit={start} className="flex-1 p-4 space-y-3 overflow-y-auto" data-testid="widget-start-form">
          <p className="text-sm text-neutral-500">Start a chat — we usually reply quickly.</p>
          <Input placeholder="Your name" value={startForm.name} onChange={(e) => setStartForm({ ...startForm, name: e.target.value })} data-testid="widget-name-input" />
          <Input type="email" required placeholder="Your email" value={startForm.email} onChange={(e) => setStartForm({ ...startForm, email: e.target.value })} data-testid="widget-email-input" />
          <Input required placeholder="How can we help?" value={startForm.message} onChange={(e) => setStartForm({ ...startForm, message: e.target.value })} data-testid="widget-message-input" />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <Button className="w-full rounded-full" data-testid="widget-start-button">Start chat</Button>
        </form>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="widget-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${m.sender === "customer" ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-900"}`}>
                  {m.body}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-[#E5E5E5] flex gap-2 shrink-0">
            <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a message…" data-testid="widget-reply-input" />
            <Button className="rounded-full px-4" data-testid="widget-send-button">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
